import type { StateTree } from 'pinia';
import { nextTick, ref } from 'vue';
import type { SimpleChoice } from '@/features/ChoicesLists/Simple/types';
import { useClientStore } from '@/features/Clients/store';
import { useFeaturesStore } from '@/features/Features/store';
import { useGlobalStore } from '@/features/Global/store';
import type { State } from '@/features/PreviousInsurance/store';
import { useInputStore } from '@/features/Sparten';
import { useTariffStore } from '@/features/Tariffs/store';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { VglError } from '@/services/errorHandler/vglError';
import { useForm } from '@/utils/form/useForm';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import type {
  LocalStorageState,
  UseLocalStorage,
} from '@/utils/useLocalStorage/types';
import { isLocalStorageState } from '@/utils/useLocalStorage/types';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import dot from 'dot-object';
import set from 'lodash/set';
import { v4 as uuid } from 'uuid';
import type { ChoiceValueValidation } from '@/features/ChoicesLists/types';
import { validateChoiceValues } from '@/features/ChoicesLists/utils';

const LOCAL_STORAGE_STATE_VERSION = 2;
const isLocalStorageAvailable = ref(false);
const transferableStateKey = 'TRANSFER_STATE';

export const useLocalStorage = (): UseLocalStorage => {
  const checkStorageIsAvailable = (): void => {
    const test = 'test';
    try {
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      isLocalStorageAvailable.value = true;
    } catch (e) {
      isLocalStorageAvailable.value = false;
    }
  };

  const removeItem = (sparte: string): void => {
    if (isLocalStorageAvailable.value) {
      const items = { ...localStorage };

      Object.keys(items).forEach((storageKey) => {
        if (storageKey.startsWith(sparte)) {
          localStorage.removeItem(storageKey);
        }
      });
    }
  };

  const storableState = useStorableState();

  const safeState = (): void => {
    const key = useGlobalStore().getSparte;
    const randomSessionId = uuid();

    const state: LocalStorageState = {
      state: {
        clients: useClientStore().entities,
        input: storableState.create(useInputStore().$state),
        tariffs: useTariffStore().$state,
        features: useFeaturesStore().$state,
      },
      version: LOCAL_STORAGE_STATE_VERSION,
    };

    localStorage[`${key}:${randomSessionId}`] = JSON.stringify(state);
  };

  const safeTransferableState = (): void => {
    localStorage[transferableStateKey] = JSON.stringify({
      input: storableState.create(useInputStore().$state),
      choicesFields: useForm().choicesFields,
    });
  };

  const loadTransferableState = (): void => {
    const item = localStorage.getItem(transferableStateKey);

    if (item !== null && item !== '') {
      const data = JSON.parse(item) as {
        input: State;
        choicesFields: typeof Map<string, SimpleChoice>;
      };
      const inputStore = useInputStore();

      try {
        const dottedData = dot.dot(storableState.read(data.input));
        const dottedInputStore = dot.dot(inputStore.$state);
        const inputStoreKeys = Object.keys(dottedInputStore);

        const { choicesFields } = useForm();

        const choiceValidation: ChoiceValueValidation = {};
        Object.entries(dottedData).forEach(([path, value]) => {
          if (inputStoreKeys.includes(path)) {
            const choice = choicesFields.get(path);
            if (choice !== undefined) {
              choiceValidation[choice] = {
                value,
                path,
              };
            }
          }
        });

        validateChoiceValues(choiceValidation).then((invalidChoices) => {
          Object.entries(dottedData).forEach(([key, value]) => {
            if (inputStoreKeys.includes(key)) {
              let add = true;
              // if path is a choice, make sure we have a valid value
              if (choicesFields.has(key)) {
                const choice = choicesFields.get(key);
                if (
                  choice === undefined ||
                  invalidChoices[choice] !== undefined
                ) {
                  add = false;
                }
              }

              if (add) {
                set(inputStore, key, value);
              }
            }
          });
        });
      } catch (e) {
        throw new VglError(VGL_ERROR_CODE.LOCAL_STORAGE_TRANSFER_FAILED, {
          cause: e,
        });
      } finally {
        localStorage.removeItem(transferableStateKey);
      }
    }
  };

  const loadState = (): void => {
    const sparte = useGlobalStore().getSparte;
    const items = { ...localStorage };

    let stateKey: string | undefined;

    Object.keys(items).forEach((storageKey) => {
      if (stateKey !== undefined) return;

      if (storageKey.startsWith(sparte)) {
        stateKey = storageKey;
      }
    });

    if (stateKey === undefined) return;

    const item = localStorage.getItem(stateKey);

    if (item !== null && item !== '') {
      const data = JSON.parse(item) as unknown;

      if (
        isLocalStorageState(data) &&
        data.version === LOCAL_STORAGE_STATE_VERSION
      ) {
        useClientStore().loadClientsFromQuoteEntities(data.state.clients);
        useTariffStore().$patch(data.state.tariffs);
        useInputStore().$patch(storableState.read(data.state.input));
        // only god knows why this is needed
        useFeaturesStore().$patch(data.state.features as StateTree);

        // make sure we reset the calculationHash to prevent backend cache mismatches
        useTariffStore().requests.lastHashCode = '';

        const config = getCurrentSpartenConfig();
        nextTick(() => {
          if (config.postLoadingSessionState !== undefined) {
            config.postLoadingSessionState(data);
          }
        });
      }
    }

    removeItem(stateKey);
  };

  const hasStoredState = (sparte: string): boolean => {
    if (isLocalStorageAvailable.value) {
      const items = { ...localStorage };

      return Object.keys(items).some((storageKey) =>
        storageKey.startsWith(sparte),
      );
    }
    return false;
  };

  return {
    checkStorageIsAvailable,
    isLocalStorageAvailable,

    safeState,
    loadState,
    removeItem,
    hasStoredState,

    safeTransferableState,
    loadTransferableState,
  };
};
